import { type JSX, useEffect, useRef, useState } from 'react'
import type { Class } from 'type-fest'
// @ts-expect-error: No types for sketchfab viewer api
import Sketchfab from '@sketchfab/viewer-api'
import type {
  SketchfabApiResponse,
  SketchfabIframeElement,
} from '@velasca/types'
import { handleError } from '@velasca/utils/handle-error'
import { trackEvent } from '@velasca/utils/track-event'
import { Iframe } from './SketchfabIframe.styles'
import type { SketchfabIframeProps } from './SketchfabIframe.types'

/**
 * NOTE: the settings of the Sketchfab viewer are the Premium settings.
 * NOTE: It's unclear what most of the Iframe props do, or if they even work at all. If issues arise, exeprimentation with them is needed.
 * NOTE: The Chrome mobile simulator is very unreliable for the Sketchfab viewer, there are various bugs, (like, you cannot move to another slide, but on the actual device you can) so it's best to test on a real device.
 * NOTE: be careful with the settings because it can be very taxing on mobile devices, to the point of crashing the browser.
 */
export const SketchfabIframe = ({
  isActive,
  isPrev,
  isNext,
  slide,
}: SketchfabIframeProps): JSX.Element => {
  const ref = useRef<HTMLIFrameElement>(null)
  const apiRef = useRef<SketchfabIframeElement | null>(null)
  const [isTracked, setIsTracked] = useState(false)
  const [isModelStarted, setIsModelStarted] = useState(false)

  useEffect(() => {
    if ((!isPrev && !isNext && !isActive) || apiRef.current) {
      return
    }

    const uid = slide.id

    const client = new (Sketchfab as Class<{
      init: (uid: string, response: SketchfabApiResponse) => void
    }>)(ref.current)

    if (!uid) {
      return
    }

    client.init(uid, {
      success: function onSuccess(api) {
        apiRef.current = api

        api.addEventListener('viewerready', () => {
          setIsModelStarted(true)
        })
      },
      error: function onError() {
        handleError(new Error('Viewer error'))
      },
      ui_animations: 0,
      ui_infos: 0,
      ui_stop: 0,
      ui_inspector: 0,
      ui_watermark_link: 0,
      ui_watermark: 0,
      ui_ar: 0,
      ui_help: 0,
      ui_settings: 0,
      ui_vr: 0,
      ui_fullscreen: 0,
      ui_annotations: 0,
      ui_theme: 'dark',
      ui_color: 185674,
      autostart: 0,
      preload: 0, // this preload the textures and some other resources. Preload should only used for critical resources. Also can cause crashed on iOS devices.
      camera: 1,
      orbit_constraint_zoom_in: 90,
      orbit_constraint_zoom_out: 180,
    })
  }, [slide.id, isPrev, isNext, isActive])

  useEffect(() => {
    // we only handle the stopping of the 3D model because it's not in autoplay mode. The user has to start it manually by clicking on the play button.
    if (!apiRef.current) {
      return
    }
    if (!isActive) {
      console.debug('stopping 3D model.')
      apiRef.current.stop()
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive || isTracked) {
      return
    }

    trackEvent({
      googleAnalytics: {
        eventName: 'sketchfab-viewer-loaded',
        data: {
          uid: slide.id,
        },
      },
    })

    setIsTracked(true)
  }, [isActive, isTracked, slide])

  return (
    <Iframe
      allowFullScreen
      // @ts-expect-error
      mozallowfullscreen="true"
      webkitallowfullscreen="true"
      allow="autoplay; fullscreen; xr-spatial-tracking"
      ref={ref}
      title={`velasca-model-${slide.id}`}
      frameBorder="0"
      id="sketchfab-iframe"
      xr-spatial-tracking="true"
      execution-while-out-of-viewport={false}
      execution-while-not-rendered={false}
      web-share="true"
      isModelStarted={isModelStarted}
    />
  )
}
